import { gtfsWeekdayIndex, serviceDateKey } from "../shared/time";
import { defaultOriginFor } from "../shared/origins";
import type { AlertConfig } from "./alerts";
import { resolveTargetWindow, TARGET_GRACE_MS } from "./target";

// SFIA (SFO) is PM-only in practice — nobody boards there in the morning — but the schedule does
// not enforce that per column; the picker just never offers it for AM.
export type ScheduledStation = "CIVC" | "GLEN" | "SFIA" | null;
export interface TravelDay { am: ScheduledStation; pm: ScheduledStation }
export interface TravelSchedule { days: TravelDay[]; version: number }
export const SCHEDULE_TIMEZONE = "America/Los_Angeles";
export const DEFAULT_TRAVEL_DAYS: readonly TravelDay[] = [
	{ am: "CIVC", pm: "GLEN" },
	{ am: "GLEN", pm: "CIVC" },
	{ am: "CIVC", pm: "CIVC" },
	{ am: "CIVC", pm: "CIVC" },
	{ am: "CIVC", pm: "GLEN" },
	{ am: null, pm: null },
	{ am: null, pm: null },
];

export function travelDays(value: unknown): TravelDay[] | null {
	if (!Array.isArray(value) || value.length !== 7) return null;
	const station = (input: unknown) => input === null || input === "CIVC" || input === "GLEN" || input === "SFIA";
	if (!value.every(day => day && typeof day === "object" && station(day.am) && station(day.pm))) return null;
	return value.map(day => ({ am: day.am, pm: day.pm }));
}

export function scheduledStation(days: readonly TravelDay[], nowMs: number, direction: "school" | "home"): ScheduledStation {
	const day = days[gtfsWeekdayIndex(serviceDateKey(nowMs, SCHEDULE_TIMEZONE))];
	return day[direction === "school" ? "am" : "pm"];
}

export function planningMorning(days: readonly TravelDay[], nowMs: number, targetMinutes: number, lateToleranceMinutes: number): { station: ScheduledStation; targetMs: number } | null {
	const activeDays = days.flatMap((day, index) => day.am === null ? [] : [index]);
	if (!activeDays.length) return null;
	const window = resolveTargetWindow({
		stationId: "ROCK", label: null, targetMinutes, earlyToleranceMinutes: 0, lateToleranceMinutes,
		days: activeDays, nowMs, timezone: SCHEDULE_TIMEZONE, earliestMs: nowMs - TARGET_GRACE_MS,
	});
	return { station: scheduledStation(days, window.targetMs, "school"), targetMs: window.targetMs };
}

export function scheduledAlertConfig(config: AlertConfig, days: readonly TravelDay[], nowMs: number): AlertConfig {
	const station = scheduledStation(days, nowMs, "school");
	const profileId = station === "GLEN" ? "glen-am" : "civic-am";
	const origin = defaultOriginFor(profileId);
	return { ...config, enabled: config.enabled && station !== null,
		profileId, origin: origin ? { lat: origin.lat, lon: origin.lon } : null,
		days: days.flatMap((day, index) => day.am === null ? [] : [index]) };
}

export function notificationDirection(payload: { kind?: string; tag?: string }): "school" | "home" | null {
	if (payload.kind === "pickup" || payload.tag?.startsWith("follow-home-")) return "home";
	if (payload.kind?.startsWith("lead-") || payload.kind?.startsWith("train-") || payload.tag?.startsWith("follow-school-")) return "school";
	return null;
}